import React, { useCallback, useMemo, useState } from 'react'
import { useTheme } from 'styled-components/native'
import { View, Image, ViewStyle, ActivityIndicator, LayoutChangeEvent } from 'react-native'
import FilePreview from 'APP/Converse/FilePreview'
import { SupportedFileWithMetadata } from 'APP/Converse/FilePreview/types'
import { chunkFilesByRow, buildFileTileMetadata } from 'APP/Lib/FileUploadHelpers'
import { getRowJustifyContent, getItemMarginRight } from 'APP/Lib/FileUploadLayoutHelpers'
import { useFileUpload } from './provider'
import Images from 'APP/Themes/Images'
import I18n from 'APP/I18n'
import {
  CenteredInstructionText,
  CenteredRequirement,
  ContentContainer,
  FilesRow,
  LoadingContainer,
} from './styles'

const DEFAULT_TILE_SIZE = 160
const MIN_TILE_SIZE = 120
const MIN_ITEMS_PER_ROW = 2

export const FileUploadContent = () => {
  const { isError, onPress, testId, files, maxFiles, onRemove, isLoading, allowMultiple } =
    useFileUpload()
  const theme = useTheme()
  const spacing = theme.metrics.baseMargin
  const [itemsPerRow, setItemsPerRow] = useState(MIN_ITEMS_PER_ROW)
  const [tileWidth, setTileWidth] = useState(DEFAULT_TILE_SIZE)

  const handleLayout = useCallback(
    ({ nativeEvent: { layout } }: LayoutChangeEvent) => {
      const availableWidth = layout.width
      if (!availableWidth) return

      const tileWithSpacing = DEFAULT_TILE_SIZE + spacing
      if (tileWithSpacing <= 0) return

      const computedItemsPerRow = Math.max(
        MIN_ITEMS_PER_ROW,
        Math.floor((availableWidth + spacing) / tileWithSpacing)
      )

      setItemsPerRow((current) => (current === computedItemsPerRow ? current : computedItemsPerRow))

      const totalSpacing = spacing * Math.max(computedItemsPerRow - 1, 0)
      const rawTileWidth = Math.floor((availableWidth - totalSpacing) / computedItemsPerRow)
      const nextTileWidth = Math.max(MIN_TILE_SIZE, Math.min(DEFAULT_TILE_SIZE, rawTileWidth))

      setTileWidth((current) => (current === nextTileWidth ? current : nextTileWidth))
    },
    [spacing]
  )

  const effectiveItemsPerRow = Math.max(MIN_ITEMS_PER_ROW, itemsPerRow)
  const hasPendingUploads = files.some(
    (file) => file?.uploadStatus && file.uploadStatus !== 'uploaded'
  )
  const effectiveFiles = useMemo<(SupportedFileWithMetadata | null)[]>(() => {
    const baseFiles: (SupportedFileWithMetadata | null)[] = [...files]
    if (isLoading && !hasPendingUploads && baseFiles.length < maxFiles) {
      baseFiles.push(null)
    }
    return baseFiles
  }, [files, hasPendingUploads, isLoading, maxFiles])

  const hasItems = effectiveFiles.length > 0
  const allUploadsComplete = !hasPendingUploads && !isLoading
  const canAddMore = files.length < maxFiles && (allowMultiple || allUploadsComplete)

  const rows = useMemo(() => {
    const chunks = chunkFilesByRow(effectiveFiles, effectiveItemsPerRow, MIN_ITEMS_PER_ROW)
    const lastRowIndex = chunks.length - 1

    return chunks.map((rowFiles, rowIndex) => {
      const isLastRow = rowIndex === lastRowIndex
      const shouldRenderInlineUpload =
        canAddMore && isLastRow && rowFiles.length < effectiveItemsPerRow

      return {
        rowFiles,
        rowIndex,
        isLastRow,
        shouldRenderInlineUpload,
      }
    })
  }, [effectiveFiles, effectiveItemsPerRow, canAddMore])

  const UploadTile: React.FC<{ style: ViewStyle; isInline?: boolean }> = ({ style, isInline }) => (
    <ContentContainer
      onPress={onPress}
      testID={testId}
      isError={isError}
      style={style}
      isInline={isInline}
    >
      <Image source={Images.fileUploadIcon} style={{ width: 40, height: 40 }} />
      <CenteredInstructionText>{I18n.t('FileUploadInput.instruction')}</CenteredInstructionText>
      <CenteredRequirement>{I18n.t('FileUploadInput.requirement')}</CenteredRequirement>
    </ContentContainer>
  )

  if (!hasItems && hasPendingUploads) {
    return (
      <LoadingContainer isError={isError}>
        <ActivityIndicator color={theme.colors.primary} />
      </LoadingContainer>
    )
  }
  if (!hasItems) {
    return <UploadTile style={{ width: '100%', minHeight: tileWidth }} />
  }

  return (
    <View style={{ width: '100%' }} onLayout={handleLayout}>
      <View style={{ width: '100%' }}>
        {rows.map(({ rowFiles, rowIndex, isLastRow, shouldRenderInlineUpload }) => {
          const isFull = rowFiles.length === effectiveItemsPerRow
          const justifyContent = getRowJustifyContent({
            isFull,
            isLastRow,
            rowFilesLength: rowFiles.length,
            shouldRenderInlineUpload,
          })
          return (
            <View key={`file-row-${rowIndex}`} style={{ marginBottom: isLastRow ? 0 : spacing }}>
              <FilesRow justifyContent={justifyContent}>
                {rowFiles.map((file, indexWithinRow) => {
                  const isLastItemInRow = indexWithinRow === rowFiles.length - 1
                  const { originalIndex, isPlaceholder, isProcessingFile, marginRight } =
                    buildFileTileMetadata(
                      file,
                      rowIndex,
                      indexWithinRow,
                      effectiveItemsPerRow,
                      isLastItemInRow,
                      shouldRenderInlineUpload,
                      spacing
                    )

                  const itemMarginRight = getItemMarginRight(
                    marginRight,
                    isLastItemInRow,
                    isFull,
                    isLastRow,
                    rowFiles.length,
                    shouldRenderInlineUpload
                  )

                  return (
                    <View
                      key={`${file?.uri ?? 'placeholder'}-${originalIndex}`}
                      style={{
                        marginRight: itemMarginRight,
                        marginBottom: spacing,
                        width: tileWidth,
                        flexShrink: 0,
                      }}
                    >
                      <FilePreview
                        file={file}
                        size={{ width: tileWidth }}
                        aspectRatio={1}
                        isLoading={isPlaceholder || isProcessingFile}
                        onClick={
                          !isPlaceholder && !isProcessingFile && onPress
                            ? () => onPress(originalIndex)
                            : undefined
                        }
                        onDelete={
                          !isPlaceholder && onRemove && file && !isProcessingFile
                            ? () => onRemove(file, originalIndex)
                            : undefined
                        }
                      />
                    </View>
                  )
                })}
                {shouldRenderInlineUpload && (
                  <View
                    style={{
                      width: tileWidth,
                      marginBottom: spacing,
                      flexShrink: 0,
                      marginRight: 0,
                      alignItems: 'stretch',
                    }}
                  >
                    <UploadTile style={{ width: tileWidth }} isInline />
                  </View>
                )}
              </FilesRow>
            </View>
          )
        })}
      </View>
      {canAddMore && !rows.some(({ shouldRenderInlineUpload }) => shouldRenderInlineUpload) && (
        <View style={{ width: '100%', marginTop: spacing }}>
          <UploadTile style={{ width: '100%', minHeight: tileWidth }} />
        </View>
      )}
    </View>
  )
}
